import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { ApiError, api } from "../api/client";

interface MatrixAnswer {
  answer: string | null;
  rating: number | null;
}

interface MatrixColleague {
  feedback_id: number;
  colleague_id: number;
  colleague_name: string;
  status: string;
  submitted_at: string | null;
  /** Absent entirely for non-submitted colleagues — never blank rows. */
  answers?: MatrixAnswer[];
}

interface Matrix {
  review_id: number;
  employee_name: string;
  cycle_name: string;
  review_period_start: string | null;
  review_period_end: string | null;
  generated_at: string;
  questions: string[];
  colleagues: MatrixColleague[];
}

const FMT = new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric" });
const shortDate = (iso: string | null) => (iso ? FMT.format(new Date(iso)) : "");

function AnswerCell({ c, idx }: { c: MatrixColleague; idx: number }) {
  if (c.status !== "submitted" || !c.answers) {
    return <span className="text-xs italic text-gray-400">Pending</span>;
  }
  const a = c.answers[idx];
  if (!a || (!a.answer && a.rating == null)) {
    return <span className="text-xs text-gray-300">—</span>;
  }
  return (
    <div>
      {a.answer && <p className="whitespace-pre-wrap text-xs text-gray-700">{a.answer}</p>}
      {a.rating != null && (
        <p className="mt-1 text-xs font-medium text-purple-600">Rating: {a.rating}/5</p>
      )}
    </div>
  );
}

export default function ColleagueFeedbackMatrix({
  reviewId,
  onClose,
}: {
  reviewId: number;
  onClose: () => void;
}) {
  const [data, setData] = useState<Matrix | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<"all" | number>("all");
  const [downloading, setDownloading] = useState<string | null>(null);

  // Synced horizontal scrollbars: a proxy bar above the table mirrors the
  // table's own (bottom) scrollbar, so a wide matrix can be scrolled from
  // either end without dragging to the bottom of a long table.
  const topBarRef = useRef<HTMLDivElement | null>(null);
  const tableWrapRef = useRef<HTMLDivElement | null>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);
  const syncingRef = useRef(false);
  const [scrollWidth, setScrollWidth] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.get<Matrix>(`/api/reviews/${reviewId}/colleague-feedback-matrix`);
      setData(d);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load colleague feedback");
    } finally {
      setLoading(false);
    }
  }, [reviewId]);

  useEffect(() => { void load(); }, [load]);

  // Esc closes the modal.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Keep the proxy scrollbar's inner width matched to the real table width.
  useLayoutEffect(() => {
    const measure = () => {
      const w = tableRef.current?.scrollWidth ?? 0;
      setScrollWidth(w);
    };
    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (ro && tableRef.current) ro.observe(tableRef.current);
    window.addEventListener("resize", measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [data, selected, loading]);

  // Two-way scroll sync, guarded so one update cannot re-trigger the other.
  const syncFrom = (source: "top" | "table") => () => {
    if (syncingRef.current) return;
    const top = topBarRef.current;
    const wrap = tableWrapRef.current;
    if (!top || !wrap) return;
    syncingRef.current = true;
    if (source === "top") wrap.scrollLeft = top.scrollLeft;
    else top.scrollLeft = wrap.scrollLeft;
    // Release on the next frame so the programmatic scroll event is ignored.
    requestAnimationFrame(() => { syncingRef.current = false; });
  };

  const download = async (fmt: "xlsx" | "docx" | "pdf") => {
    setDownloading(fmt);
    try {
      const resp = await fetch(
        `/api/reviews/${reviewId}/colleague-feedback-matrix/export?fmt=${fmt}`,
        { credentials: "include" },
      );
      if (!resp.ok) throw new Error("Export failed");
      const blob = await resp.blob();
      const cd = resp.headers.get("content-disposition") ?? "";
      const match = /filename="?([^"]+)"?/.exec(cd);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = match?.[1] ?? `colleague-feedback.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError("Export failed. Please try again.");
    } finally {
      setDownloading(null);
    }
  };

  const single = typeof selected === "number"
    ? data?.colleagues.find((c) => c.feedback_id === selected)
    : undefined;
  const showTable = !loading && !!data && data.colleagues.length > 0 && !single;

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/60 p-3 sm:p-5"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Colleague feedback"
    >
      <div
        className="flex h-full w-full flex-col overflow-hidden rounded-xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Title bar — fixed */}
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
          <div className="min-w-0">
            <h2 className="truncate text-lg font-semibold text-gray-900">
              {data
                ? `${data.employee_name} — Colleague Feedback (${data.cycle_name})`
                : "Colleague Feedback"}
            </h2>
            {data?.review_period_start && data?.review_period_end && (
              <p className="mt-0.5 text-xs text-gray-500">
                Review period {shortDate(data.review_period_start)} – {shortDate(data.review_period_end)}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            title="Close (Esc)"
            className="shrink-0 rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Controls — fixed, always visible */}
        <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-gray-100 px-5 py-3">
          <select
            value={selected === "all" ? "all" : String(selected)}
            onChange={(e) => setSelected(e.target.value === "all" ? "all" : Number(e.target.value))}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm"
          >
            <option value="all">Show All (comparison)</option>
            {(data?.colleagues ?? []).map((c) => (
              <option key={c.feedback_id} value={c.feedback_id}>
                {c.colleague_name}{c.status !== "submitted" ? " (Pending)" : ""}
              </option>
            ))}
          </select>

          {selected !== "all" && (
            <button
              type="button"
              onClick={() => setSelected("all")}
              className="rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              Show All
            </button>
          )}

          <span className="ml-auto flex items-center gap-1.5">
            <span className="mr-1 text-xs text-gray-400">Download</span>
            {(["xlsx", "docx", "pdf"] as const).map((f) => (
              <button
                key={f}
                type="button"
                disabled={downloading !== null}
                onClick={() => void download(f)}
                className="rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {downloading === f ? "…" : f === "xlsx" ? "Excel" : f === "docx" ? "Word" : "PDF"}
              </button>
            ))}
          </span>
        </div>

        {error && (
          <p className="shrink-0 border-b border-red-100 bg-red-50 px-5 py-2 text-sm text-red-700">{error}</p>
        )}

        {/* Proxy top scrollbar — fixed, mirrors the table's own scrollbar */}
        {showTable && (
          <div
            ref={topBarRef}
            onScroll={syncFrom("top")}
            className="shrink-0 overflow-x-auto overflow-y-hidden border-b border-gray-100"
            style={{ height: 14 }}
            aria-hidden="true"
          >
            <div style={{ width: scrollWidth, height: 1 }} />
          </div>
        )}

        {/* Body */}
        {loading ? (
          <div className="flex-1 p-5"><p className="text-sm text-gray-400">Loading…</p></div>
        ) : !data || data.colleagues.length === 0 ? (
          <div className="flex-1 p-5">
            <p className="text-sm text-gray-400">No colleagues assigned to this review.</p>
          </div>
        ) : single ? (
          /* Single colleague view */
          <div className="flex-1 overflow-auto p-5">
            <div className="mb-3 flex items-center gap-2">
              <h3 className="text-sm font-semibold text-gray-900">{single.colleague_name}</h3>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                single.status === "submitted"
                  ? "bg-green-50 text-green-700"
                  : "bg-amber-50 text-amber-700"
              }`}>
                {single.status === "submitted"
                  ? `Submitted ${shortDate(single.submitted_at)}`
                  : "Pending"}
              </span>
            </div>
            {single.status !== "submitted" ? (
              <p className="text-sm italic text-gray-400">
                This colleague has not submitted their feedback yet.
              </p>
            ) : (
              <div className="space-y-3">
                {data.questions.map((q, i) => (
                  <div key={i} className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                    <p className="text-xs font-medium text-gray-600">{q}</p>
                    <div className="mt-1"><AnswerCell c={single} idx={i} /></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* Comparison table — scrolls in both axes; question column sticky
             left, colleague header row sticky top. */
          <div
            ref={tableWrapRef}
            onScroll={syncFrom("table")}
            className="min-h-0 flex-1 overflow-auto"
          >
            <table ref={tableRef} className="border-separate border-spacing-0">
              <thead>
                <tr>
                  <th className="sticky left-0 top-0 z-30 w-72 min-w-[18rem] border-b border-r border-gray-200 bg-white p-2.5 text-left text-xs font-semibold text-gray-700">
                    Question
                  </th>
                  {data.colleagues.map((c) => (
                    <th
                      key={c.feedback_id}
                      className={`sticky top-0 z-20 min-w-[16rem] border-b border-gray-200 p-2.5 text-left text-xs font-semibold ${
                        c.status === "submitted"
                          ? "bg-white text-gray-700"
                          : "bg-amber-50 text-amber-800"
                      }`}
                    >
                      {c.colleague_name}
                      <span className="mt-0.5 block text-[10px] font-normal">
                        {c.status === "submitted" ? shortDate(c.submitted_at) : "Pending"}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.questions.map((q, i) => (
                  <tr key={i}>
                    <td className="sticky left-0 z-10 border-b border-r border-gray-100 bg-white p-2.5 align-top text-xs font-medium text-gray-600">
                      {q}
                    </td>
                    {data.colleagues.map((c) => (
                      <td
                        key={c.feedback_id}
                        className={`border-b border-gray-100 p-2.5 align-top ${
                          c.status === "submitted" ? "" : "bg-amber-50/40"
                        }`}
                      >
                        <AnswerCell c={c} idx={i} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
